import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  Signal,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateService } from '@ngx-translate/core';
import { GridColumn, PrimeGridOption } from '@grid/abstractions';
import { Client } from '@shared/models';
import { HeaderService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { Collection } from 'src/app/modules/admin/models';
import { SecurityAudit } from 'src/app/modules/portfolio/models/security-audit';
import { SecurityAuditService } from '../../services/security-audit.service';
import { PrimeGridComponent } from '@shared/modules/grid/components/prime-grid/prime-grid.component';
import { AuditHistoryHeadersService } from '../../services/audit-history-headers.service';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';

@Component({
  standalone: false,
  selector: 'gstar-audit-history',
  templateUrl: './audit-history.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditHistoryComponent implements OnInit {
  readonly securityAuditService = inject(SecurityAuditService);
  private readonly translateService = inject(TranslateService);
  private readonly headerService = inject(HeaderService);
  private readonly auditHistoryHeaderService = inject(
    AuditHistoryHeadersService
  );
  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly destroyRef = inject(DestroyRef);

  securitiesAudit = signal<Collection<SecurityAudit>>({
    total: 0,
    items: [],
  });

  isLoading = signal<boolean>(true);
  headers = signal<GridColumn[]>([]);

  grid: Signal<PrimeGridComponent<SecurityAudit> | undefined> =
    viewChild<PrimeGridComponent<SecurityAudit>>('grid');

  setting: PrimeGridOption<SecurityAudit> = {
    lazy: true,
    emptyMessage: this.translateService.instant(
      'securityAudit.noAuditHistoryMessage'
    ),
    hasCheckbox: false,
  };

  ngOnInit(): void {
    this.headerService.selectedClient$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((client: Client | undefined) => {
        this.updateSelectedClient(client);
      });

    this.securityAuditService.auditSecuritiesByClient
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securities: Collection<SecurityAudit>) => {
        this.loadAuditSecurities(securities);
      });

    this.headers.set(this.auditHistoryHeaderService.getHeaders());
  }

  queryHandler(query: Partial<QueryOptions<SecurityAudit>>): void {
    this.securityAuditService.updateQuery(query);
  }

  private updateSelectedClient(client: Client | undefined): void {
    this.securityAuditService.updateClient(client);
    if (this.grid()) {
      this.grid()!.clearFiltersAndRun();
    }

    if (!this.securityAuditService.isFirst()) {
      this.investmentJurisdictionService.reloadCountryList();
    }
  }

  private loadAuditSecurities(
    auditSecurities: Collection<SecurityAudit>
  ): void {
    this.securitiesAudit.set(auditSecurities);
    this.isLoading.set(false);
  }
}
